/**
 * UnconfirmedLiabilitiesPanel.js
 *
 * Shared UI for surfacing accepted MTD submissions that have no confirmed
 * VATLiability row yet. Used in two places:
 *   1. As a persistent, non-dismissible banner on VatDashboard / VATSubmission
 *      (variant="banner") when GET /hmrc/unconfirmed-liabilities reports
 *      unconfirmed_count > 0.
 *   2. As a full takeover (variant="takeover") when /hmrc/submit returns
 *      409 BLOCKED_UNCONFIRMED_LIABILITY, replacing the review screen until
 *      every listed period is confirmed.
 *
 * This intentionally has no "skip" or "dismiss" affordance anywhere in this
 * file. The override path (override_unconfirmed_liability_block) is a
 * deliberate break-glass exception and is NOT implemented here — per the
 * handover, it should live behind a separate Advanced/Support affordance,
 * not as a peer option next to confirmation.
 */

import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    AlertTitle,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
    CircularProgress,
    Chip
} from '@mui/material';
import { Warning as WarningIcon, CheckCircle } from '@mui/icons-material';
import api from './axiosConfig';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2
    }).format(amount || 0);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB');
};

/**
 * Props:
 * - submissions: array of { submission_id, period_start, period_end, box3_total_vat_due,
 *                            box4_vat_reclaimed, box5_net_vat_due, submitted_at }
 * - variant: 'banner' | 'takeover'
 * - onAllConfirmed: called once every submission in the list has been confirmed
 *   (banner: re-fetch / hide; takeover: let the caller resume the original flow)
 */
const UnconfirmedLiabilitiesPanel = ({ submissions, variant = 'banner', onAllConfirmed }) => {
    const [confirmingId, setConfirmingId] = useState(null);
    const [confirmedIds, setConfirmedIds] = useState([]);
    const [errorBySubmission, setErrorBySubmission] = useState({});

    const remaining = submissions.filter(s => !confirmedIds.includes(s.submission_id));

    const handleConfirm = async (submissionId) => {
        setConfirmingId(submissionId);
        setErrorBySubmission(prev => ({ ...prev, [submissionId]: null }));

        try {
            await api.post(`/hmrc/submissions/${submissionId}/confirm-liability`);
            const updated = [...confirmedIds, submissionId];
            setConfirmedIds(updated);

            if (updated.length === submissions.length && onAllConfirmed) {
                onAllConfirmed();
            }
        } catch (err) {
            console.error('Failed to confirm liability', err);
            setErrorBySubmission(prev => ({
                ...prev,
                [submissionId]: err.response?.data?.message || err.response?.data?.error || 'Failed to confirm liability. Please try again.'
            }));
        } finally {
            setConfirmingId(null);
        }
    };

    if (remaining.length === 0) {
        return null;
    }

    const periodLabel = (s) =>
        `${formatDate(s.period_start)} – ${formatDate(s.period_end)}`;

    if (variant === 'banner') {
        return (
            <Alert
                severity="warning"
                icon={<WarningIcon />}
                sx={{
                    mb: 3,
                    border: '2px solid #f59e0b',
                    '& .MuiAlert-message': { width: '100%' }
                }}
            >
                <AlertTitle sx={{ fontWeight: 700 }}>
                    Action required — {remaining.length} VAT {remaining.length === 1 ? 'liability' : 'liabilities'} not yet confirmed
                </AlertTitle>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    HMRC has accepted the following submission{remaining.length === 1 ? '' : 's'}, but the resulting VAT
                    liability has not been confirmed in SalonLogic Pro yet. You will not be able to submit a new VAT
                    return until this is resolved.
                </Typography>

                {remaining.map((s) => (
                    <Box
                        key={s.submission_id}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1,
                            p: 1.5,
                            mb: 1,
                            bgcolor: 'rgba(255,255,255,0.6)',
                            borderRadius: 1
                        }}
                    >
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {periodLabel(s)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Net VAT due: {formatCurrency(s.box5_net_vat_due)} · Submitted {formatDate(s.submitted_at)}
                            </Typography>
                            {errorBySubmission[s.submission_id] && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                    {errorBySubmission[s.submission_id]}
                                </Typography>
                            )}
                        </Box>
                        <Button
                            variant="contained"
                            size="small"
                            color="warning"
                            disabled={confirmingId === s.submission_id}
                            startIcon={confirmingId === s.submission_id ? <CircularProgress size={16} color="inherit" /> : null}
                            onClick={() => handleConfirm(s.submission_id)}
                        >
                            {confirmingId === s.submission_id ? 'Confirming…' : 'Confirm Liability'}
                        </Button>
                    </Box>
                ))}
            </Alert>
        );
    }

    // variant === 'takeover'
    return (
        <Box sx={{ maxWidth: 700, mx: 'auto' }}>
            <Paper elevation={0} sx={{ p: 4, border: '2px solid #f59e0b', bgcolor: '#fffbeb' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                    <WarningIcon sx={{ color: '#d97706', fontSize: 32 }} />
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                            Confirm previous VAT liability before continuing
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            HMRC has already accepted a VAT return for the period below, but the liability has not
                            been confirmed in SalonLogic Pro. This must be confirmed before a new return can be
                            submitted.
                        </Typography>
                    </Box>
                </Box>

                <Table aria-label="Unconfirmed VAT liabilities">
                    <TableHead>
                        <TableRow>
                            <TableCell component="th" scope="col">Period</TableCell>
                            <TableCell component="th" scope="col" align="right">Net VAT Due</TableCell>
                            <TableCell component="th" scope="col" align="right">Submitted</TableCell>
                            <TableCell component="th" scope="col" align="right">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {remaining.map((s) => (
                            <TableRow key={s.submission_id}>
                                <TableCell>{periodLabel(s)}</TableCell>
                                <TableCell align="right">{formatCurrency(s.box5_net_vat_due)}</TableCell>
                                <TableCell align="right">{formatDate(s.submitted_at)}</TableCell>
                                <TableCell align="right">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="warning"
                                        disabled={confirmingId === s.submission_id}
                                        startIcon={confirmingId === s.submission_id ? <CircularProgress size={16} color="inherit" /> : null}
                                        onClick={() => handleConfirm(s.submission_id)}
                                    >
                                        {confirmingId === s.submission_id ? 'Confirming…' : 'Confirm'}
                                    </Button>
                                    {errorBySubmission[s.submission_id] && (
                                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                            {errorBySubmission[s.submission_id]}
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
};

export default UnconfirmedLiabilitiesPanel;